import React, { useCallback, useMemo, useState } from 'react';
import { Grid } from '@mui/material';
import { ProfileInfo } from '../components/profile/profileInfo';
import { ViewModeSetting } from '../components/profile/viewModeSetting';
import { AppToolbox } from '../components/app/appToolbox';
import { AppMain } from '../layout/appMain';
import {
  PREFERENCES,
  ProfilePreferences,
  ProfilePreferencesField,
  ViewMode,
} from '../models/preferences';
import SaveIcon from '@mui/icons-material/Save';
import { AppConfirmationSuccessful } from '../components/app/confirmation/appConfirmationSuccessful';
import { useSelector } from 'react-redux';
import {
  savePreferenceThunk,
  selectPreferences,
} from '../store/preferencesSlice';
import { selectProfile } from '../store/profileSlice';
import { useAppDispatch } from '../hooks';
import useAppSnackbar from '../hooks/useAppSnackbar';
import { PreferredProjectTaskSetting } from '../components/profile/preferredProjectTaskSetting';
import { ExportSettings } from '../components/profile/exportSettings';
import { AppButtonContained } from '../components/app/button/appButtonContained';
import { AutocompleteOption } from '../models/autocompleteOption';
import { Task } from '../models/task';
import { RolesNamesBE } from '../models/employee';

export function PageProfile(): JSX.Element {
  const [openSuccessDialog, setOpenSuccessDialog] = useState(false);

  const preference = useSelector(selectPreferences);
  const [currentPreference, setCurrentPreference] =
    useState<ProfilePreferences>(preference);

  const myProfile = useSelector(selectProfile);

  const dispatch = useAppDispatch();

  const { onErrorSnackbar } = useAppSnackbar();

  const canSeeExportSettings = useMemo(() => {
    if (!myProfile) return false;
    
    const allowedRoles = [
      RolesNamesBE.PROJECT_MANAGER,
      RolesNamesBE.SALES_MANAGER,
      RolesNamesBE.TEAM_LEAD,
      RolesNamesBE.ADMIN,
    ];
    
    return myProfile.permissionGroups.some((role) =>
      allowedRoles.includes(role.name)
    );
  }, [myProfile]);

  const handlePreferredProjectTaskChange = useCallback(
    (field: ProfilePreferencesField, value: AutocompleteOption | Task | null) => {
      setCurrentPreference((prev) => {
        if (field === ProfilePreferencesField.PROJECT && !value) {
          return {
            ...prev,
            [ProfilePreferencesField.PROJECT]: null,
            [ProfilePreferencesField.TASK]: null,
          };
        }

        return {
          ...prev,
          [field]: value,
        };
      });
    },
    []
  );

  const handleViewModeChange = useCallback((value: string) => {
    setCurrentPreference((prev) => {
      return {
        ...prev,
        [ProfilePreferencesField.VIEW_MODE]: PREFERENCES.indexOf(
          value as ViewMode
        ),
      };
    });
  }, []);

  const handleExportSettingsChange = useCallback(
    (field: ProfilePreferencesField, value: boolean) => {
      setCurrentPreference((prev) => ({
        ...prev,
        [field]: value,
      }));
    },
    []
  );

  const handleSavePreference = useCallback(() => {
    dispatch(savePreferenceThunk(currentPreference))
      .unwrap()
      .then(() => {
        setOpenSuccessDialog(true);
      })
      .catch((error) => {
        onErrorSnackbar(error);
      })
      .finally(() => {
        setTimeout(() => {
          setOpenSuccessDialog(false);
        }, 1000);
      });
  }, [currentPreference, dispatch, onErrorSnackbar]);

  const handleCloseSnackbar = useCallback(() => {
    if (openSuccessDialog) {
      setOpenSuccessDialog(false);
    }
  }, [openSuccessDialog]);

  const headerButtons = useMemo(() => {
    return [{ icon: SaveIcon, onClick: handleSavePreference }];
  }, [handleSavePreference]);

  return (
    <AppMain buttons={headerButtons}>
      <AppConfirmationSuccessful
        onClose={handleCloseSnackbar}
        message="Saved successfully!"
        isOpen={openSuccessDialog}
      />

      <Grid container spacing={2}>
        <Grid item sm={12}>
          <AppToolbox
            pageTitle="My profile"
            secondChildren={
              <AppButtonContained
                label="Save"
                sx={{ width: '130px' }}
                handleClick={handleSavePreference}
              />
            }
          />
        </Grid>
        <Grid item sm={7}>
          {myProfile && <ProfileInfo profile={myProfile} />}
        </Grid>
        <Grid item sm={5}>
          {currentPreference && (
            <ViewModeSetting
              preference={PREFERENCES[currentPreference.activityView]}
              onChange={handleViewModeChange}
            />
          )}
        </Grid>

        <Grid item sm={canSeeExportSettings ? 7 : 12}>
          {myProfile && (
            <PreferredProjectTaskSetting
              employeeId={myProfile.id}
              preferredProjectId={
                currentPreference[ProfilePreferencesField.PROJECT]?.id || ''
              }
              preferredTaskId={
                currentPreference[ProfilePreferencesField.TASK]?.id || ''
              }
              onFieldChange={handlePreferredProjectTaskChange}
            />
          )}
        </Grid>

        {canSeeExportSettings && (
          <Grid item sm={5}>
            {currentPreference && (
              <ExportSettings
                exportShowContractType={
                  currentPreference[ProfilePreferencesField.EXPORT_SHOW_CONTRACT_TYPE]
                }
                exportShowProjectManager={
                  currentPreference[ProfilePreferencesField.EXPORT_SHOW_PROJECT_MANAGER]
                }
                onFieldChange={handleExportSettingsChange}
              />
            )}
          </Grid>
        )}
      </Grid>
    </AppMain>
  );
}
